import { useSecureGraphqlLazyQuery, useSecureGraphqlMutation } from '@psyomics/ui-common';

import {
  DeleteReferralRequestSurveyAttachmentDocument,
  DeleteReferralRequestSurveyAttachmentInput,
  GetReferralRequestSurveyAttachmentDownloadUrlDocument,
  GetReferralRequestSurveyAttachmentDownloadUrlInput,
  GetReferralRequestSurveyAttachmentDownloadUrlQuery,
  GetReferralRequestSurveyResponseAttachmentsDocument,
  GetReferralRequestSurveyResponseAttachmentsQuery,
} from '../generated/survey';

const useDeleteReferralRequestSurveyAttachment = () =>
  useSecureGraphqlMutation<
    { deleteReferralRequestSurveyAttachment: boolean },
    { input: DeleteReferralRequestSurveyAttachmentInput },
    'AttachmentNotFound'
  >(DeleteReferralRequestSurveyAttachmentDocument, ['AttachmentNotFound' as const]);

const useGetReferralRequestSurveyAttachmentDownloadUrl = () =>
  useSecureGraphqlLazyQuery<
    GetReferralRequestSurveyAttachmentDownloadUrlQuery,
    { input: GetReferralRequestSurveyAttachmentDownloadUrlInput }
  >(GetReferralRequestSurveyAttachmentDownloadUrlDocument);

const useGetReferralRequestSurveyResponseAttachments = () =>
  useSecureGraphqlLazyQuery<
    GetReferralRequestSurveyResponseAttachmentsQuery,
    { referralRequestSurveyResponseId: string }
  >(GetReferralRequestSurveyResponseAttachmentsDocument);

async function uploadReferralRequestSurveyAttachment({
  file,
  questionName,
  referralApiUrl,
  referralRequestSurveyResponseId,
}: {
  file: File;
  questionName: string;
  referralApiUrl: string;
  referralRequestSurveyResponseId: string;
}): Promise<{
  contentType: string;
  fileName: string;
  id: string;
  parentId: string;
  sizeBytes: number;
}> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(
    `${referralApiUrl}/survey-responses/${referralRequestSurveyResponseId}/questions/${questionName}/attachments`,
    {
      body: formData,
      credentials: 'include',
      method: 'POST',
    }
  );

  if (!response.ok) {
    const body: { message?: string; messageKey?: string } = await Promise.resolve()
      .then(() => response.json())
      .catch(() => ({}));
    throw new Error(
      body.messageKey || body.message || `Failed to upload referral request survey attachment: ${response.status}`
    );
  }

  return response.json();
}

export {
  uploadReferralRequestSurveyAttachment,
  useDeleteReferralRequestSurveyAttachment,
  useGetReferralRequestSurveyAttachmentDownloadUrl,
  useGetReferralRequestSurveyResponseAttachments,
};
